import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import PublicIcon from "@mui/icons-material/Public";
import GroupsIcon from "@mui/icons-material/Groups";
import BadgeIcon from "@mui/icons-material/Badge";
import { Localize } from "../../../app/lang/langs";
import { getNewsAudienceOptions } from "../../../containers/dashboard/dashboardSlice";
import { checkPermission } from "../../../app/utils/helpers";
import { config, NEWS_AUDIENCE_ALL, NEWS_AUDIENCE_COLLABS } from "../../../app/utils/config";

/**
 * Localized label of one audience option.
 *
 * All and Collabs are product vocabulary and get translation keys; a manager profile is
 * data and keeps the name the backend stores, the way profiles are shown everywhere else
 * in the app (permissions tabs, evaluation config).
 */
export function audienceOptionLabel(option) {
    if (!option) return ""
    if (option.value === NEWS_AUDIENCE_ALL) return Localize("NEWS_AUDIENCE_ALL")
    if (option.value === NEWS_AUDIENCE_COLLABS) return Localize("NEWS_AUDIENCE_COLLABS")
    return option.name || ""
}

function AudienceIcon({ option, sx }) {
    if (option.value === NEWS_AUDIENCE_ALL) return <PublicIcon sx={sx} />
    if (option.value === NEWS_AUDIENCE_COLLABS) return <GroupsIcon sx={sx} />
    return <BadgeIcon sx={sx} />
}

/**
 * Who the post is for: All, Collabs, or any number of manager profiles.
 *
 * `value` is the flat list the backend parses — 'all' | 'collabs' | profile id — not the
 * option objects, so what the composer holds is exactly what it posts.
 *
 * All is exclusive by construction: it already includes everybody, so selecting it drops
 * whatever else was picked and picking anything else drops it. Leaving the field empty is
 * not a valid state either — an empty selection would be a post nobody can read — so
 * clearing it falls back to All rather than to nothing.
 */
export default function NewsAudiencePicker({ value, onChange, disabled }) {

    const dispatch = useDispatch()
    const options = useSelector((state) => state.dashboard.newsAudienceOptions)

    // The route is behind `news` EDIT, so asking for the options without that permission is
    // a request that can only come back 403. Only an author reaches this component today,
    // but the check keeps that true if it is ever reused.
    useEffect(() => {
        if ((!options || !options.length) &&
            checkPermission(config.scopes.news, config.permissionTypes.edit)) {
            dispatch(getNewsAudienceOptions())
        }
    }, [])

    // The picker works on option objects; the value it exposes is the flat list. An id the
    // options don't know (a profile deleted since the post was composed) is dropped rather
    // than rendered as a blank chip.
    const selected = useMemo(() => {
        const byValue = new Map((options || []).map(option => [String(option.value), option]))
        return (value || [])
            .map(entry => byValue.get(String(entry)))
            .filter(Boolean)
    }, [value, options])

    const handleChange = (_, newSelection) => {
        // "All" wins whenever it was just added; otherwise it is dropped as soon as a
        // narrower target joins it.
        const justAddedAll = newSelection.length > selected.length &&
            newSelection.some(option => option.value === NEWS_AUDIENCE_ALL) &&
            !selected.some(option => option.value === NEWS_AUDIENCE_ALL)

        if (justAddedAll || !newSelection.length) {
            onChange([NEWS_AUDIENCE_ALL])
            return
        }
        onChange(newSelection
            .filter(option => option.value !== NEWS_AUDIENCE_ALL)
            .map(option => option.value))
    }

    return (
        <Box>
            <Autocomplete
                multiple
                disableCloseOnSelect
                options={options || []}
                value={selected}
                onChange={handleChange}
                disabled={disabled}
                getOptionLabel={audienceOptionLabel}
                isOptionEqualToValue={(option, selectedOption) =>
                    String(option.value) === String(selectedOption.value)}
                renderOption={(optionProps, option) => {
                    const { key, ...rest } = optionProps
                    return (
                        <li key={String(option.value)} {...rest}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <AudienceIcon option={option} sx={{ fontSize: 18, color: "#6b6560" }} />
                                <Typography sx={{ fontSize: "0.85rem" }}>
                                    {audienceOptionLabel(option)}
                                </Typography>
                            </Box>
                        </li>
                    )
                }}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => {
                        const { key, ...rest } = getTagProps({ index })
                        return (
                            <Chip
                                key={String(option.value)}
                                {...rest}
                                size="small"
                                icon={<AudienceIcon option={option} sx={{ fontSize: 16 }} />}
                                label={audienceOptionLabel(option)}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "0.72rem",
                                    color: "#002880",
                                    backgroundColor: "rgba(0, 40, 128, 0.07)",
                                    border: "1px solid rgba(0, 40, 128, 0.16)",
                                    "& .MuiChip-icon": { color: "#002880" },
                                }}
                            />
                        )
                    })
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={Localize("NEWS_AUDIENCE")}
                        placeholder={Localize("NEWS_AUDIENCE_PLACEHOLDER")}
                        size="small"
                    />
                )}
            />
            <Typography sx={{ fontSize: "0.75rem", color: "#6b6560", mt: 0.75, pl: 0.5 }}>
                {Localize("NEWS_AUDIENCE_HINT")}
            </Typography>
        </Box>
    )
}
